import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, forkJoin } from 'rxjs';

import { BaseIndexPageComponent } from 'src/app/components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from 'src/app/components/pages/base-index-page/base-index-page.config';
import { DataTableColumnText } from 'src/app/components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import { DataTableColumnBadge } from 'src/app/components/widgets/data-table/columns/data-table-column-badge/data-table-column-badge.config';
import { DataTableColumnLink } from 'src/app/components/widgets/data-table/columns/data-table-column-link/data-table-column-link.config';
import { DataTableColumnActions } from 'src/app/components/widgets/data-table/columns/data-table-column-actions/data-table-column-actions.config';
import ApiOptionsConfig from 'src/app/services/api/api-options.config';
import { RegulatorsService } from 'src/app/services/api/regulators.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CommonService } from 'src/app/services/common.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { RouteService } from 'src/app/services/route.service';
import {
  Regulator,
  RegulatorClient,
  RegulatorClientWebsite,
  regulatorStatusBadgeClass,
  websiteAnalysisStatusLabel,
  websiteScanStatusLabel
} from 'src/app/models/entities/regulator';
import { RegulatorContextBoxComponent, RegulatorContextItem } from '../components/regulator-context-box/regulator-context-box.component';
import { RegulatorWebsiteFormComponent } from '../components/regulator-website-form/regulator-website-form.component';
import { apiErrorMessage, toWebsiteHref, withDeleteFeedback } from '../regulators-ui.utils';

@Component({
  selector: 'app-regulator-client-websites-page',
  imports: [BaseIndexPageComponent, RegulatorContextBoxComponent, RegulatorWebsiteFormComponent],
  templateUrl: './regulator-client-websites-page.component.html',
  styleUrl: './regulator-client-websites-page.component.scss',
})
export class RegulatorClientWebsitesPageComponent implements OnInit, OnDestroy {

  @ViewChild(BaseIndexPageComponent) private indexPage?: BaseIndexPageComponent;
  @ViewChild('websiteModal') private websiteModal!: TemplateRef<unknown>;

  baseIndexPageConfig: BaseIndexPageConfig;
  contextItems: RegulatorContextItem[] = [];
  regulator: Regulator | null = null;
  editingWebsite: RegulatorClientWebsite | null = null;

  readonly regulatorId: number;
  readonly clientId: number;

  /** Adapter used by the data-table delete button (nested endpoint + toasts). */
  readonly deleteService = {
    delete: (websiteId: number) =>
      withDeleteFeedback(this.regulatorsService.deleteWebsite(this.regulatorId, websiteId), this.alertNotificationService, 'Website')
  };

  private scanningWebsiteIds = new Set<number>();
  private modalRef?: NgbModalRef;
  private subscriptions = new Subscription();

  constructor(
    protected regulatorsService: RegulatorsService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private alertNotificationService: AlertNotificationService,
    private commonService: CommonService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.regulatorId = Number(this.route.snapshot.paramMap.get('regulatorId'));
    this.clientId = Number(this.route.snapshot.paramMap.get('clientId'));

    this.baseIndexPageConfig = {
      pageTitle: 'Regulators',
      cardTitle: 'Websites',
      dataTable: {
        data: [],
        pagination: null,
        searchModel: {
          url: null,
          scanStatus: null,
          analysisStatus: null,
        },
        columns: [
          new DataTableColumnLink({
            attribute: 'url',
            header: 'Website',
            filter: 'text',
            value: (item: RegulatorClientWebsite) => item?.url ?? '',
            href: (item: RegulatorClientWebsite) => toWebsiteHref(item?.url)
          }),
          new DataTableColumnBadge({
            attribute: 'scanStatus',
            header: 'Scan Status',
            filter: 'select',
            filterOptionsSource: 'websiteScanStatuses',
            value: (item: RegulatorClientWebsite) => item?.scanStatus ? websiteScanStatusLabel(item.scanStatus) : null,
            badgeClass: (item: RegulatorClientWebsite) => regulatorStatusBadgeClass(item?.scanStatus)
          }),
          new DataTableColumnText({
            attribute: 'lastScanTime',
            header: 'Last Scan',
            value: (item: RegulatorClientWebsite) => this.commonService.formatDateValue(item?.lastScanTime, true)
          }),
          new DataTableColumnBadge({
            attribute: 'analysisStatus',
            header: 'Analysis Status',
            filter: 'select',
            filterOptionsSource: 'websiteAnalysisStatuses',
            value: (item: RegulatorClientWebsite) => item?.analysisStatus ? websiteAnalysisStatusLabel(item.analysisStatus) : null,
            badgeClass: (item: RegulatorClientWebsite) => regulatorStatusBadgeClass(item?.analysisStatus)
          }),
          new DataTableColumnText({
            attribute: 'lastAnalysisTime',
            header: 'Last Analysis',
            value: (item: RegulatorClientWebsite) => this.commonService.formatDateValue(item?.lastAnalysisTime, true)
          }),
          new DataTableColumnBadge({
            attribute: 'scanEnabled',
            header: 'Scan Enabled',
            sortable: false,
            width: '130px',
            value: (item: RegulatorClientWebsite) => item?.scanEnabled ? 'Yes' : 'No',
            badgeClass: (item: RegulatorClientWebsite) => regulatorStatusBadgeClass(item?.scanEnabled ? 'COMPLETED' : null)
          }),
          new DataTableColumnActions({
            header: 'Actions',
            attribute: 'actions',
            sortable: false,
            cellClass: 'text-center',
            style: { width: '200px' },
            buttons: [
              {
                icon: 'fa-history',
                tooltip: 'Scan history',
                aclAction: 'view',
                onClick: (item: RegulatorClientWebsite) =>
                  this.routeService.regulatorWebsiteScans(this.regulatorId, this.clientId, item.websiteId)
              },
              {
                icon: 'fa-play',
                tooltip: 'Scan now',
                aclAction: 'modify',
                onClick: (item: RegulatorClientWebsite) => this.scanNow(item)
              },
              {
                icon: 'fa-edit',
                tooltip: 'Edit website',
                aclAction: 'modify',
                onClick: (item: RegulatorClientWebsite) => this.openWebsiteForm(item)
              },
              {
                icon: 'fa-trash',
                tooltip: 'Delete website',
                aclAction: 'delete',
                isDelete: true
              },
            ]
          }),
        ]
      },
      apiListService: (options: ApiOptionsConfig) =>
        this.regulatorsService.listClientWebsites(this.regulatorId, this.clientId, options),
      responseListModelName: 'websites',
    };
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.mainLayoutService.activeProfile$.subscribe(profileCode => {
        const canModify = this.accessControlService.hasFeatureActionAccess(profileCode, 'REGULATORS_LIST', 'modify');
        this.baseIndexPageConfig.addNewItemButton = canModify
          ? { type: 'success', title: 'Add Website', onClick: () => this.openWebsiteForm(null) }
          : undefined;
      })
    );

    forkJoin({
      regulator: this.regulatorsService.detail(this.regulatorId),
      client: this.regulatorsService.detailClient(this.regulatorId, this.clientId),
    }).subscribe({
      next: ({ regulator, client }: any) => this.applyContext(regulator?.data?.regulator, client?.data?.client),
      error: () => this.router.navigate(['/error/404'])
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.modalRef?.dismiss();
    this.mainLayoutService.setBreadcrumbs(null);
  }

  goBack(): void {
    this.routeService.regulatorClients(this.regulatorId);
  }

  openWebsiteForm(website: RegulatorClientWebsite | null): void {
    this.editingWebsite = website;
    this.modalRef = this.modalService.open(this.websiteModal, { centered: true, backdrop: 'static' });
    this.modalRef.result.catch(() => undefined);
  }

  closeWebsiteForm(): void {
    this.modalRef?.dismiss();
  }

  onWebsiteSaved(): void {
    this.modalRef?.close();
    this.indexPage?.reload();
  }

  scanNow(website: RegulatorClientWebsite): void {
    if (this.regulator && !this.regulator.externalCode) {
      this.alertNotificationService.warning('Scans cannot be started: the regulator has no external code configured.');
      return;
    }
    if (this.scanningWebsiteIds.has(website.websiteId)) {
      return;
    }

    this.scanningWebsiteIds.add(website.websiteId);
    this.regulatorsService.startScan(this.regulatorId, this.clientId, website.websiteId).subscribe({
      next: () => {
        this.scanningWebsiteIds.delete(website.websiteId);
        this.alertNotificationService.success('Scan queued');
        this.indexPage?.reload();
      },
      error: (error: any) => {
        this.scanningWebsiteIds.delete(website.websiteId);
        this.alertNotificationService.error(apiErrorMessage(error, 'Error starting the scan'));
      }
    });
  }

  private applyContext(regulator: Regulator | null | undefined, client: RegulatorClient | null | undefined): void {
    if (!regulator || !client) {
      return;
    }

    this.regulator = regulator;
    this.baseIndexPageConfig.cardTitle = `Websites – ${client.name}`;
    this.contextItems = [
      { label: 'Regulator', code: regulator.code, value: regulator.name },
      { label: 'Client', code: client.code, value: client.name },
    ];
    this.mainLayoutService.setBreadcrumbs([
      { label: 'Regulators', routerLink: '/regulators' },
      { label: regulator.code },
      { label: 'Clients', routerLink: `/regulators/${regulator.regulatorId}/clients` },
      { label: client.name },
    ]);
  }
}
